import PropTypes from 'prop-types'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'

// Translations system
import { appWithTranslation } from 'next-i18next'
import nextI18NextConfig from '../next-i18next.config.js'

// context
import { LazyImageProvider } from '@/context/LazyImageContext'
import AppProvider, { AppContext } from '@/context/AppContext'
import FilterProvider from '@/context/FilterContext'
import ModalProvider from '@/context/ModalContext'
import NavigationProvider, { NavigationContext } from '@/context/NavigationContext'
import PrintProvider from '@/context/PrintContext'
import UserProvider from '@/context/UserContext'
import { SessionProvider } from 'next-auth/react'

// global styles
import './../styles/global.scss'

// import icons
import '../components/Svg'

// Internal components
import InitialLoader from '../components/InitialLoader/InitialLoader'
import Navigation from '../components/Navigation/Navigation'
import PageTransition from '../components/PageTransition/PageTransition'
import Footer from '../components/Footer/Footer'
import Modal from '../components/Modal/Modal'
import UserController from '../components/UserController/UserController'
import AppController from '../components/AppController/AppController'
import ConsentManager from '../components/ConsentManager/ConsentManager'
import CookieMessage from '../components/CookieMessage/CookieMessage'
import Notification from '../components/Notification/Notification'
import CalendarProvider from '@/context/CalendarContext'
import FallbackLoader from '../components/FallbackLoader/FallbackLoader'
import ToastMessage from '../components/ToastMessage/ToastMessage'
import FeedbackModal from '../components/FeedbackModal/FeedbackModal'
import Seo from '../components/Seo/Seo'
import LoginWall from '../components/LoginWall/LoginWall'
import PageError from '../components/PageError/PageError'
import Maintenance from '../components/Maintenance/Maintenance'
import MatchProvider from '@/context/MatchContext'

const RinusApp = (props) => {
  const { Component, pageProps } = props
  const seo = pageProps?.entry?.seomatic ? pageProps?.entry?.seomatic : pageProps?.seomatic
  const router = useRouter()
  const cmpid = process.env.NEXT_PUBLIC_CONSENT_MANAGER_ID
  const theme = process.env.NEXT_PUBLIC_THEME
  const federationFonts = {
    knvb: 'baron',
    frf: 'exo',
    sportwide: 'raleway'
  }

  if (process.env.NEXT_PUBLIC_BLOCK_RENDER === 'true') {
    return (
      <LazyImageProvider>
        <Maintenance />
      </LazyImageProvider>
    )
  }

  const isMatch = pageProps?.entry?.typeHandle === 'match'
  const accountPage = router.asPath.startsWith('/account/')

  return (
    <>
      <Head>
        <link
          rel="prefetch"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
          href={`/fonts/${federationFonts[theme]}-light-1-0.woff2`}
        />
        <link
          rel="prefetch"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
          href={`/fonts/${federationFonts[theme]}-regular-1-0.woff2`}
        />
        <link
          rel="prefetch"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
          href={`/fonts/${federationFonts[theme]}-bold-1-0.woff2`}
        />
      </Head>
      <SessionProvider session={pageProps.session}>
        <UserProvider session={pageProps.session}>
          <AppProvider loggedIn={pageProps?.session?.loggedIn}>
            <MatchProvider
              enabled={pageProps?.session?.matchMode}
              // User team information & match mode data
              team={{ ...pageProps?.session?.team }}
              // Pass match data
              match={isMatch && pageProps.entry ? pageProps?.entry : {}}
            >
              <LazyImageProvider>
                <>
                  {pageProps.error ? (
                    <PageError errorCode={pageProps.errorCode} />
                  ) : (
                    <FilterProvider>
                      <ModalProvider>
                        <NavigationProvider>
                          <PrintProvider>
                            <CalendarProvider>
                              {seo && <Seo {...seo} />}
                              <InitialLoader />
                              <Navigation {...pageProps.navigation} />
                              <PageTransition />
                              {router.isFallback ? (
                                <FallbackLoader />
                              ) : (
                                <>
                                  <AppContext.Consumer>
                                    {({ isRestricted, isExcluded }) => {
                                      return isRestricted &&
                                        (pageProps.statusCode !== 404 || pageProps.statusCode !== 500) &&
                                        !isExcluded ? (
                                        <LoginWall {...pageProps.loginWall} />
                                      ) : (
                                        <Component {...pageProps} />
                                      )
                                    }}
                                  </AppContext.Consumer>
                                </>
                              )}
                              {pageProps.footer && !accountPage && <Footer {...pageProps.footer} />}

                              {/* KNVB Cookie Message (ConsentManager), NON iOS Only */}
                              <AppContext.Consumer>
                                {({ isLatestiOSApp }) =>
                                  cmpid && !isLatestiOSApp ? <ConsentManager cmpid={cmpid} /> : ''
                                }
                              </AppContext.Consumer>

                              {/* Default Cookie Message */}
                              <NavigationContext.Consumer>
                                {({ cookiesConsent }) =>
                                  !cmpid && (!cookiesConsent || cookiesConsent?.length <= 0) ? <CookieMessage /> : ''
                                }
                              </NavigationContext.Consumer>

                              {/* App Notifications */}
                              {pageProps.notification?.toggle === 'true' && (
                                <NavigationContext.Consumer>
                                  {({ notificationSeen }) =>
                                    notificationSeen ? '' : <Notification {...pageProps.notification} />
                                  }
                                </NavigationContext.Consumer>
                              )}
                              <Modal />
                              <UserController />
                              <AppController />
                              <ToastMessage />
                              {!accountPage && <FeedbackModal />}
                            </CalendarProvider>
                          </PrintProvider>
                        </NavigationProvider>
                      </ModalProvider>
                    </FilterProvider>
                  )}
                </>
              </LazyImageProvider>
            </MatchProvider>
          </AppProvider>
        </UserProvider>
      </SessionProvider>
      {/* Google Tag Manager - Global base code */}
      {/* Only load GTM if the env var is set since there are different versions of the site */}
      {process.env.NEXT_PUBLIC_GTM_ID ? (
        <Script
          id="gtag-base"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID}');
          `
          }}
        />
      ) : null}
    </>
  )
}

RinusApp.propTypes = {
  Component: PropTypes.func,
  pageProps: PropTypes.object
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// RinusApp.getInitialProps = async (appContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);
//
//   return { ...appProps }
// }

export default appWithTranslation(RinusApp, nextI18NextConfig)
